/**
 * @file ThemeContext.jsx
 * @description React context for theme management.
 * Provides theme switching, localStorage persistence, and CSS variable application.
 */

import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { parseTheme, applyTheme, getThemeMeta } from '../utils/themeParser';
import zapierThemeContent from '../themes/zapier.md.js';
import defaultThemeContent from '../themes/default.md.js';
import nintendoThemeContent from '../themes/nintendo.md.js';

// Static import map for theme markdown files.
// Each theme is stored as a raw string exported from JS wrapper modules
// for CRA compatibility (CRA cannot import .md as raw text without ejecting).

const ThemeContext = createContext(null);

const themeFiles = {
  'bms-default': defaultThemeContent,
  'zapier': zapierThemeContent,
  'nintendo': nintendoThemeContent,
};

/**
 * ThemeProvider component that manages theme state, applies CSS custom properties,
 * and persists the active theme selection to localStorage.
 */
export function ThemeProvider({ children }) {
  // Build available themes list from the theme files
  const availableThemes = Object.entries(themeFiles).map(([key, content]) => {
    const meta = getThemeMeta(content);
    return {
      id: key,
      name: meta.name,
      description: meta.description,
    };
  });

  // Initialize active theme from localStorage or default to 'bms-default'
  const [activeTheme, setActiveThemeState] = useState(() => {
    try {
      const stored = localStorage.getItem('bms_theme');
      if (stored && themeFiles[stored]) {
        return stored;
      }
    } catch (e) {
      // localStorage may not be available
    }
    return 'bms-default';
  });

  // Wrap setActiveTheme to also persist to localStorage
  const setActiveTheme = useCallback((themeId) => {
    if (themeFiles[themeId]) {
      setActiveThemeState(themeId);
      try {
        localStorage.setItem('bms_theme', themeId);
      } catch (e) {
        // localStorage may not be available
      }
    }
  }, []);

  // Apply theme CSS variables whenever activeTheme changes
  useEffect(() => {
    const content = themeFiles[activeTheme];
    if (content) {
      const cssVars = parseTheme(content);
      applyTheme(cssVars);
    }
  }, [activeTheme]);

  const contextValue = {
    activeTheme,
    setActiveTheme,
    availableThemes,
  };

  return (
    <ThemeContext.Provider value={contextValue}>
      {children}
    </ThemeContext.Provider>
  );
}

/**
 * Hook to access the theme context.
 * @returns {{ activeTheme: string, setActiveTheme: function, availableThemes: Array }}
 */
export function useTheme() {
  const context = useContext(ThemeContext);
  if (!context) {
    throw new Error('useTheme must be used within a ThemeProvider');
  }
  return context;
}

export default ThemeContext;
